import React, { useState, useEffect, useRef } from 'react';
import {
  Fab,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Collapse,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  CircularProgress,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useLocation } from 'react-router-dom';
import apiService from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';

const STORAGE_KEY = 'feedbackWidgetSeen';
// Sűrű, gombokkal teli munkafelületek: itt a gomb kicsi és halvány, telefonon el is tűnik,
// hogy ne takarja a kártyák műveleti gombjait
const COMPACT_ROUTES = ['/wedding-planner', '/admin'];

export default function FeedbackWidget() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const compact = COMPACT_ROUTES.some((path) => location.pathname.startsWith(path));

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const successTimerRef = useRef(null);
  const collapseTimerRef = useRef(null);

  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen && !compact) {
      setExpanded(true);
      collapseTimerRef.current = setTimeout(() => {
        setExpanded(false);
        localStorage.setItem(STORAGE_KEY, '1');
      }, 5000);
    }
    return () => {
      clearTimeout(collapseTimerRef.current);
      clearTimeout(successTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpen = () => {
    clearTimeout(collapseTimerRef.current);
    setExpanded(false);
    localStorage.setItem(STORAGE_KEY, '1');
    setOpen(true);
    setSuccess(false);
    setError('');
    setText('');
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setError('');
    try {
      await apiService.post(API_ENDPOINTS.BUG_REPORTS.CREATE, {
        description: text.trim(),
        pageUrl: window.location.pathname,
      });
      setSuccess(true);
      setText('');
      successTimerRef.current = setTimeout(() => {
        setOpen(false);
        setSuccess(false);
      }, 3000);
    } catch {
      setError('Nem sikerült elküldeni. Kérjük, próbáld újra.');
    } finally {
      setLoading(false);
    }
  };

  const fabSx = {
    bgcolor: '#edbe5f',
    color: '#1b331f',
    '&:hover': { bgcolor: '#d4a94e' },
    boxShadow: 3,
    transition: 'all 0.3s ease',
    ...(compact && {
      width: 36,
      height: 36,
      minHeight: 36,
      boxShadow: 1,
      opacity: 0.7,
      '&:hover': { bgcolor: '#d4a94e', opacity: 1 },
    }),
  };

  if (compact && isMobile) return null;

  const formContent = (
    <Box sx={{ p: 2.5, width: isMobile ? '100%' : 320 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} color="#1b331f">
          Hiba bejelentés
        </Typography>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {success ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, gap: 1 }}>
          <CheckCircleOutlineIcon sx={{ color: '#2a5d32', fontSize: 48 }} />
          <Typography variant="body2" color="text.secondary" align="center">
            Köszönjük a visszajelzést! Hamarosan javítjuk.
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
            Az oldal jelenleg béta fázisban üzemel. Ha valami nem működik, írd le!
          </Typography>
          <TextField
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            label="Miben tapasztaltál hibát?"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 1000))}
            helperText={error || `${text.length}/1000`}
            error={!!error}
            size="small"
            sx={{ mb: 1.5 }}
          />
          <Button
            fullWidth
            variant="contained"
            disabled={!text.trim() || loading}
            onClick={handleSubmit}
            sx={{
              bgcolor: '#1b331f',
              color: '#edbe5f',
              '&:hover': { bgcolor: '#2a5d32' },
              '&:disabled': { bgcolor: '#ccc' },
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Küldés'}
          </Button>
        </>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: compact ? 12 : 24,
        right: compact ? 12 : 24,
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 1,
      }}
    >
      {/* Desktop popover */}
      {!isMobile && (
        <Collapse in={open} unmountOnExit>
          <Paper
            elevation={4}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              mb: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {formContent}
          </Paper>
        </Collapse>
      )}

      {/* FAB gomb */}
      <Fab
        variant={expanded && !compact ? 'extended' : 'circular'}
        size={compact ? 'small' : 'large'}
        onClick={handleOpen}
        sx={fabSx}
        aria-label="Hibát találtál?"
      >
        <BugReportIcon sx={{ mr: expanded && !compact ? 1 : 0, fontSize: compact ? 18 : undefined }} />
        {expanded && !compact && (
          <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
            Hibát találtál?
          </Typography>
        )}
      </Fab>

      {/* Mobil bottom drawer */}
      {isMobile && (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          disableSwipeToOpen
          PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
        >
          <Box sx={{ pt: 1, pb: 'env(safe-area-inset-bottom, 16px)' }}>
            <Box sx={{ width: 40, height: 4, bgcolor: 'divider', borderRadius: 2, mx: 'auto', mb: 1 }} />
            {formContent}
          </Box>
        </SwipeableDrawer>
      )}
    </Box>
  );
}
